import { getResourceManager, ResourceType } from './ResourceManager';
import { getUmdExportResolver } from './UmdExportResolver';
import type { Resource } from './Resource';
import { ElementNode } from './ElementNode';
import { ScriptNode } from './ScriptNode';
import { StyleNode } from './StyleNode';
import { LifecycleFns } from './Def';
import { getAtomic } from './Atomic';

import { promiseIgnoreCatch } from './utils/promiseIgnoreCatch';
import { Debugger } from './utils/Debugger';
import { fixCssUrl } from './utils/css';

const grm = getResourceManager();

interface ScriptAndStyle {
    scripts: ScriptNode[];
    styles: StyleNode[];
}

export class Page<CustomProps> extends Debugger {
    #name: string;
    #scripts: ScriptNode[];
    #styles: StyleNode[];

    #styleElements: HTMLStyleElement[] = [];

    #styleResources: Resource[] = [];

    #isDestroyed = false;

    constructor(options: Partial<ScriptAndStyle> & { name: string }) {
        super();
        this.#name = options.name;
        this.#scripts = options.scripts || [];
        this.#styles = options.styles || [];
    }

    protected get debugName(): string {
        return `page:${this.#name}`;
    }

    public get [Symbol.toStringTag](): string {
        return 'Page';
    }

    public override toString(): string {
        return `${this[Symbol.toStringTag]}(${this.#name})`;
    }

    public async resolveLifecycle(
        strict: boolean,
        fetchResourceOptions?: RequestInit | ((src: string) => RequestInit),
        env?: Record<string | symbol, unknown>
    ): Promise<LifecycleFns<CustomProps>> {
        this.debug('Call resolveLifecycle(%O).', strict);
        const invalid: ElementNode[] = [];
        const scripts: ScriptNode[] = [];
        const styles: StyleNode[] = [];
        const entries: ScriptNode[] = [];

        this.#scripts.forEach(node => {
            (node.isValid ? scripts : invalid).push(node);
            if (node.isValid && node.isEntry) entries.push(node);
        });

        if (entries.length > 1) {
            this.debug('Unexpected redundant entries:\n%s.', entries.join('\n'));
            throw Error(`${this} has unexpected redundant entries.`);
        }

        this.#styles.forEach(node => (node.isValid ? styles : invalid).push(node));

        if (this.debug.enabled && invalid.length > 0) {
            this.debug('Invalid style or script:\n%s.', invalid.join('\n'));
        }

        const entry = entries.length ? entries[0] : scripts[scripts.length - 1];

        if (!entry) throw Error(`${this} has no js entry.`);

        let entryIndex = -1;
        const depScripts: ScriptNode[] = [];
        const nonDepScripts: ScriptNode[] = [];

        scripts.forEach((s, index) => {
            if (s === entry) {
                entryIndex = index;
                return;
            }

            let isDep = false;

            if (entry.isAsync) {
                isDep = entryIndex === -1 && !s.isAsync && !s.isDefer;
            } else if (entry.isDefer) {
                isDep = (entryIndex === -1 && !s.isAsync) || (entryIndex > -1 && !s.isAsync && !s.isDefer);
            } else {
                isDep = entryIndex === -1 && !s.isAsync && !s.isDefer;
            }

            (isDep ? depScripts : nonDepScripts).push(s);
        });

        const createFetchResourceOptions = (src: string): RequestInit => {
            if ('function' === typeof fetchResourceOptions) {
                return fetchResourceOptions(src);
            }

            return fetchResourceOptions || {};
        };

        if (this.debug.enabled) {
            this.debug('Styles and dependency scripts:\n%s.', [...styles, ...depScripts, entry].join('\n'));
        }

        this.#styleResources = [];

        // 🛎
        if (strict) {
            // ⬇️ Pre-downloading scripts.
            setTimeout(() =>
                [entry, ...depScripts].forEach(s => s.src && s.downloadContent(createFetchResourceOptions(s.src)))
            );

            // ⬇️ Downloading styles should block process.
            await Promise.all(styles.map(s => s.href && s.downloadContent(createFetchResourceOptions(s.href))));
            // Downloading contents costs so much time, we have to check if this page has already been destroyed.
            if (this.#isDestroyed) throw Error(`${this} has been destroyed.`);
        }

        this.debug('Resolve styles.');
        // If strict, downloaded content will be used to create <style>, or else <link> first.
        this.#resolveStyles(styles, !strict);

        // 🛎
        if (strict) {
            // ⬇️ Downloading scripts should block process, but they are probably downloaded already.
            await Promise.all(
                [entry, ...depScripts].map(s => s.src && s.downloadContent(createFetchResourceOptions(s.src)))
            );
        }

        const runJS = async (): Promise<LifecycleFns<CustomProps>> => {
            // depScripts will never contain async scripts.
            if (depScripts.length > 0) {
                this.debug('Evaluate dependency scripts');
                // In strict mode, scripts must be evaluated in sequence.
                await this.#execBatchScripts(depScripts, !strict, !strict);
            }

            // Evaluate non-dependency scripts, don't block process.
            setTimeout(async () => {
                if (!nonDepScripts.length) {
                    return;
                }

                this.debug('Evaluate non-dependency scripts:\n%s.', nonDepScripts.join('\n'));

                // 🛎
                if (strict) {
                    // ⬇️ Download non-dependency scripts.
                    await Promise.all(
                        nonDepScripts.map(s => s.src && s.downloadContent(createFetchResourceOptions(s.src)))
                    );
                }

                this.#execBatchScripts(nonDepScripts, !strict, !strict);
            });

            this.debug('Evaluate entry script: %s.', entry);
            const [entryExport] = await Promise.all([
                this.#execEntryScript(entry, !strict),
                ...this.#styleResources.map(s => s.ref(this)),
            ]);
            return entryExport;
        };

        const envLoading = {
            // respect to qiankun
            __POWERED_BY_BLUEBELL__: true,
            ...env,
        };

        const keys = Object.getOwnPropertyNames(envLoading);
        const symbols = Object.getOwnPropertySymbols(envLoading);

        const savedDescriptors: Record<string | symbol, PropertyDescriptor> = {};

        for (const key of [...keys, ...symbols]) {
            const des = Object.getOwnPropertyDescriptor(window, key);
            if (des) savedDescriptors[key] = des;
        }

        this.debug(`savedDescriptors: %O.`, savedDescriptors);

        return await getAtomic().commit(
            Promise.resolve()
                .then(() => {
                    this.debug('Inject environment variables: %O.', envLoading);
                    for (const key of [...keys, ...symbols]) {
                        try {
                            const desInEnv = Object.getOwnPropertyDescriptor(envLoading, key);
                            if (desInEnv) Object.defineProperty(window, key, desInEnv); // Must exist.
                        } catch (e) {
                            console.warn(`Save ${String(key)} in window failed: `, e);
                        }
                    }
                })
                .then(() => runJS())
                .finally(() => {
                    this.debug('Recover environment variables: %O.', envLoading);
                    for (const key of [...keys, ...symbols]) {
                        try {
                            if (!Reflect.has(savedDescriptors, key)) {
                                if (!Reflect.deleteProperty(window, key)) {
                                    throw Error(`Failed to delete ${String(key)} from global.`);
                                }
                            } else {
                                Object.defineProperty(window, key, savedDescriptors[key]);
                            }
                        } catch (e) {
                            console.warn(`Recover ${String(key)} in global faild: `, e);
                        }
                    }
                })
        );
    }

    public show(): Promise<void> {
        this.#styleElements.forEach(ele => document.head.appendChild(ele));
        return Promise.all(this.#styleResources.map(sr => sr.ref(this))).then();
    }

    public hide(): Promise<void> {
        this.#styleElements.forEach(ele => ele.parentElement?.removeChild(ele));
        return Promise.all(this.#styleResources.map(sr => sr.deref(this))).then();
    }

    public destroy(): Promise<void> {
        this.#isDestroyed = true;
        return promiseIgnoreCatch(this.hide()).then(() => {
            this.#styleResources = [];
            this.#styleElements = [];
        });
    }

    async #resolveStyles(styles: StyleNode[], externalFirst: boolean): Promise<void> {
        for (const style of styles) {
            if (externalFirst && style.href) {
                this.#styleResources.push(
                    grm.createResourceIfNotExists(style.href, ResourceType.CSS, style.crossOrigin)
                );
                continue;
            }

            const styleElement = document.createElement('style');
            styleElement.setAttribute('data-bluebell-belong-to', this.#name);

            let comment = '';

            if (style.href) {
                comment += `\n/* ${style.href} */\n`;
            }

            const text = document.createTextNode(comment + fixCssUrl(style.content, style.href || style.owner));
            styleElement.appendChild(text);

            if (style.media) {
                styleElement.media = style.media;
            }

            this.#styleElements.push(styleElement);
        }
    }

    async #execBatchScripts(scripts: ScriptNode[], parallel: boolean, externalFirst: boolean): Promise<void> {
        // Parallel mode
        if (parallel) {
            // TODO think about order
            await Promise.all(scripts.map(script => this.#execScript(script, externalFirst)));
            return;
        }

        let i = 0;

        const fun = async (): Promise<unknown> => {
            if (i < scripts.length) {
                await this.#execScript(scripts[i], externalFirst);
                i++;
                return await fun();
            }
        };

        await fun();
    }

    async #execEntryScript(script: ScriptNode, externalFirst: boolean): Promise<LifecycleFns<CustomProps>> {
        if (script.isESM) {
            return this.#evalESM<LifecycleFns<CustomProps>>(script.content, script.src);
        }

        const entryName = (await getUmdExportResolver().resolve(() => {
            if (externalFirst && script.src) {
                return grm.createResourceIfNotExists(script.src, ResourceType.JS, script.crossOrigin).ref(this);
            }

            this.#evalUMD(script.content, script.src);
        }, script.src || script.content)) as keyof Window;

        this.debug('Global exported: "%s".', entryName);

        const exported = window[entryName] as LifecycleFns<CustomProps>;

        return exported;
    }

    #execScript(script: ScriptNode, externalFirst: boolean): Promise<unknown> {
        if (script.isESM) {
            // Always externalFirst
            return this.#evalESM(script.content, script.src);
        } else {
            if (externalFirst && script.src) {
                return grm.createResourceIfNotExists(script.src, ResourceType.JS, script.crossOrigin).ref(this);
            }
            return Promise.resolve(this.#evalUMD(script.content, script.src));
        }
    }

    #evalUMD(content: string, src?: string): void {
        this.#createInjectedScriptComment(
            src ? `Inject ${src} from ${this.#name}` : `Inject inline code from ${this.#name}.`
        );
        // FIXME find existing sourceURL
        const sourceURL = src && !src.match(/^\/\/# sourceURL=/m) ? `\n//# sourceURL=${src}\n` : '';
        (0, eval)(`;${content}${sourceURL}`);
    }

    // TODO support inline ESM
    // Use dynamic import if src exists,
    // since evalating ESM content is quite unreliable now.
    #evalESM<Ret = void>(content: string, src?: string): Promise<Ret> {
        if (src) {
            this.#createInjectedScriptComment(`Import ${src} from ${this.#name}`);
            return import(/* @vite-ignore */ /* webpackIgnore: true */ src) as Promise<Ret>;
        } else {
            this.#createInjectedScriptComment(`Import inline code from ${this.#name}.`);
            const uri = URL.createObjectURL
                ? URL.createObjectURL(new Blob([content], { type: 'text/javascript' }))
                : `data:text/javascript;charset=utf-8,${encodeURIComponent(content)}`;
            return import(/* @vite-ignore */ /* webpackIgnore: true */ uri) as Promise<Ret>;
        }
    }

    #createInjectedScriptComment(commentStr: string): void {
        const comment = document.createComment(commentStr);
        document.body.appendChild(comment);
    }
}
